<script setup lang="ts">
import { UiBadge, UiButton } from "@packages/component-library";
import { computed, ComputedRef, ref, watch } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useRoute } from "vue-router";
import { IconBagFill, IconExitRegular } from "@packages/icon-library";
import { IUser, useUserStore } from "@/modules/user-module";
import { getUserAvatar } from "@/package/global-helpers/user-helpers";
import { useCartStore } from "@/modules/cart-module";
import { tariffsInfo } from "@/package/global-constants/tariffs-info";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const route = useRoute();
const showUserDropDown = ref(false);
const target = ref<any>(null);

const userStore = useUserStore();
const cartStore = useCartStore();

const user: ComputedRef<null | IUser> = computed(() => userStore.user);

watch(route, () => {
  showUserDropDown.value = false;
});

onClickOutside(target, () => {
  showUserDropDown.value = false;
});
</script>

<template>
  <div v-if="user" ref="target" class="header-profile">
    <RouterLink
      v-if="cartStore.getCartProductsLength"
      :to="{ name: 'CartView' }"
      class="cart-button"
    >
      <span>
        {{
          cartStore.getCartProductsLength <= 9
            ? cartStore.getCartProductsLength
            : "9+"
        }}
      </span>
      <IconBagFill />
    </RouterLink>

    <button
      type="button"
      class="header-user"
      @click="showUserDropDown = !showUserDropDown"
    >
      <span
        v-if="getUserAvatar(user)"
        class="avatar"
        :style="{ backgroundImage: `url(${getUserAvatar(user)})` }"
      />
    </button>

    <div v-if="showUserDropDown" class="header-profile__drop-down">
      <p>
        {{ t("header.tariff") }}
        <UiBadge
          size="m"
          :label="user.tariff_name"
          :color="tariffsInfo[user.tariff_code].color"
          type="soft"
        >
          <component
            :is="tariffsInfo[user.tariff_code].icon"
            v-if="
              user.tariff_code !== 'vip_d' && user.tariff_code !== 'vip_gold_d'
            "
          />
        </UiBadge>
      </p>
      <a :href="user.passport_profile_link" target="_blank">
        {{ t("nav.user-data") }}
      </a>
      <RouterLink :to="{ name: 'ProfileInvite' }">
        {{ t("nav.profile") }}
      </RouterLink>
      <RouterLink :to="{ name: 'ProfileScores' }">
        {{ t("nav.scores") }}
      </RouterLink>
      <span></span>
      <button type="button" class="exit" @click="userStore.exitUser()">
        <IconExitRegular /> {{ t("system.exit") }}
      </button>
    </div>
  </div>

  <UiButton
    v-else
    :label="t('system.auth')"
    size="m"
    class="auth-button"
    color="secondary"
    data-test-id="header-button-auth"
    @click="userStore.authUser()"
  />
</template>

<style scoped lang="scss">
.header-profile {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: $base-space * 4;
  position: relative;
  margin: 0 0 0 auto;

  &__drop-down {
    position: absolute;
    width: 256px;
    right: 0;
    top: calc(100% + 8px);
    padding: ($base-space * 2) 0;
    border-radius: 8px;
    background-color: var(--color-semantic-background-normal-bg-primary);
    box-shadow: 0 0 30px 0 rgba(169, 169, 169, 0.15);
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    align-items: flex-start;
    z-index: 5;

    > span {
      width: 100%;
      height: 1px;
      margin: calc($base-space / 2) 0 0;
      background-color: var(--color-semantic-border-normal-soft);
    }

    > p {
      width: 100%;
      padding: ($base-space * 3) ($base-space * 4);
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: $base-space * 2;
      @include body-s();
      color: var(--color-semantic-content-normal-primary);
    }

    > a,
    button {
      width: 100%;
      padding: ($base-space * 2) ($base-space * 4);
      @include body-m();
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: $base-space * 2;
      color: var(--color-semantic-content-normal-primary);
      transition: all 0.1s ease-out;

      @media (hover: hover) {
        &:hover {
          background-color: var(--color-semantic-background-normal-secondary);
        }
      }

      > .icon {
        width: 20px;
        height: 20px;
        background-color: var(--color-semantic-content-normal-tertiary);
      }
    }
  }
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0.5px solid var(--color-semantic-border-normal-soft);
  @include centerBackgroundImageCover();
}

.header-user {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @include body-bold-m();
  color: var(--color-semantic-content-normal-primary);
  gap: calc($base-space * 3);
}

.auth-button {
  margin: 0 0 0 auto;
}

.cart-button {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  > span:not(.icon) {
    position: absolute;
    top: 0;
    right: 0;
    width: 16px;
    height: 16px;
    border-radius: 16px;
    background-color: var(--color-semantic-background-normal-brand);
    display: flex;
    align-items: center;
    justify-content: center;
    @include caption();
    color: var(--color-semantic-content-normal-static-dark);
  }
}
</style>
